import React, { useEffect, useState, useRef } from 'react';

interface LyricsDisplayProps {
  lrcContent: string;
  currentTime: number;
}

interface LyricLine {
  time: number;
  text: string;
  endTime?: number; // Optional end time for the lyric
}

const TIMING_OFFSET = 0.5; // Show lyrics 0.5 seconds earlier

export const LyricsDisplay: React.FC<LyricsDisplayProps> = ({ lrcContent, currentTime }) => {
  const [lyrics, setLyrics] = useState<LyricLine[]>([]);
  const [currentLineIndex, setCurrentLineIndex] = useState(-1);
  const [initialized, setInitialized] = useState(false);
  const lastUpdateRef = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Parse lyrics on component mount or when lrcContent changes
  useEffect(() => {
    try {
      if (!lrcContent) {
        setLyrics([]);
        return;
      }

      const manuallyParsedLyrics = parseLrcManually(lrcContent);
      
      // Calculate end times for each lyric line
      const lyricsWithEndTimes = manuallyParsedLyrics.map((lyric, index, arr) => {
        if (index < arr.length - 1) {
          return { ...lyric, endTime: arr[index + 1].time };
        }
        // For the last lyric, set an end time 5 seconds after it starts
        return { ...lyric, endTime: lyric.time + 5 };
      });
      
      setLyrics(lyricsWithEndTimes);
      setInitialized(true);
    } catch (error) {
      console.error('Error parsing lyrics:', error);
      setLyrics([]);
    }
  }, [lrcContent]);

  // Update current lyric based on playback time (with timing offset)
  useEffect(() => {
    if (!initialized || lyrics.length === 0) return;

    // Apply timing offset to show lyrics earlier
    const adjustedTime = currentTime + TIMING_OFFSET;

    // Find the current lyric based on time
    let index = -1;
    for (let i = 0; i < lyrics.length; i++) {
      if (adjustedTime >= lyrics[i].time &&
         (i === lyrics.length - 1 || adjustedTime < lyrics[i + 1].time)) {
        index = i;
        break;
      }
    }

    setCurrentLineIndex(prevIndex => {
      if (index !== prevIndex) {
        return index;
      }
      return prevIndex;
    });
  }, [currentTime, lyrics, initialized]);

  // Parse LRC formatted lyrics
  const parseLrcManually = (lrcContent: string): LyricLine[] => {
    if (!lrcContent) return [];
    
    const lines = lrcContent.split('\n');
    // Match both standard LRC format [MM:SS.MS] and timing brackets <MM:SS.MS>
    const timeRegex = /(\[\d{2}:\d{2}\.\d{2,3}\]|<\d{2}:\d{2}\.\d{2,3}>)/g;
    const parsedLines: LyricLine[] = [];
    
    const bracketRegex = /\[(\d{2}):(\d{2})\.(\d{2,3})\]/g;

    lines.forEach(line => {
      const bracketMatches = [...line.matchAll(bracketRegex)];
      if (bracketMatches.length === 0) return;

      const text = line.replace(timeRegex, '').trim();
      if (!text) return;

      bracketMatches.forEach(match => {
        const minutes = parseInt(match[1], 10);
        const seconds = parseInt(match[2], 10);
        const msStr = match[3];
        const milliseconds = parseInt(msStr, 10) / (msStr.length === 2 ? 100 : 1000);
        const time = minutes * 60 + seconds + milliseconds;
        parsedLines.push({ time, text });
      });
    });
    
    // Sort by time to ensure correct order
    return parsedLines.sort((a, b) => a.time - b.time);
  };

  // Display lyrics with scrolling effect (bottom to top)
  const renderLyrics = () => {
    if (lyrics.length === 0) {
      return (
        <div className="text-center text-white/50 h-12 flex items-center justify-center px-4">
          No lyrics available
        </div>
      );
    }

    const fixedLineHeight = 80;
    const scrollOffset = currentLineIndex >= 0 ? -(currentLineIndex * fixedLineHeight) : 0;

    return (
      <div
        className="flex flex-col w-full px-4 transition-transform duration-500 ease-out"
        style={{ transform: `translateY(${scrollOffset}px)` }}
      >
        {lyrics.map((line, idx) => {
          const distanceFromCurrent = idx - (currentLineIndex >= 0 ? currentLineIndex : 0);
          const isCurrentLine = distanceFromCurrent === 0 && currentLineIndex >= 0;

          let className = "text-center transition-all duration-300 w-full flex items-center justify-center ";

          if (isCurrentLine) {
            className += "text-5xl font-bold text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]";
          } else if (distanceFromCurrent === 1) {
            className += "text-3xl font-semibold text-white/80 drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)]";
          } else if (distanceFromCurrent === 2) {
            className += "text-2xl font-medium text-white/65 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]";
          } else if (distanceFromCurrent === 3) {
            className += "text-xl text-white/50 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]";
          } else if (distanceFromCurrent > 3) {
            className += "text-lg text-white/30 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]";
          } else {
            className += "text-lg text-white/0";
          }

          return (
            <div
              key={`${line.time}-${idx}`}
              className={className}
              style={{ height: `${fixedLineHeight}px` }}
            >
              {line.text}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="w-full h-full">
      <div
        ref={containerRef}
        className="relative overflow-hidden h-full flex items-start pt-32 w-full"
      >
        {renderLyrics()}
      </div>
    </div>
  );
};